import { FieldType } from "@smart/bridge-types-basic";
import {
  isNotNullOrUndefined,
  partition,
  toSentenceCase,
} from "@smart/itops-utils-basic";

import { GqlMatterLayout, MatterField, NewFieldOptions } from "../../types";
import { ContactType } from "../../utils";
import { mapMatterField } from "../mapped-fields-modal/helpers";

export type AvailableField = {
  name: string;
  label: string;
  type: FieldType;
  matterField: MatterField;
};

const getPathSegments = (name: string, contactType: ContactType): string[] => {
  const prefix = `${contactType}/`;
  const path = name.startsWith(prefix) ? name.slice(prefix.length) : name;

  return path.split("/").filter(Boolean);
};

export const buildFieldPathLabel = (name: string, contactType: ContactType) =>
  getPathSegments(name, contactType).map(toSentenceCase).join(" / ");

export const isNestedFieldName = (name: string, contactType: ContactType) =>
  getPathSegments(name, contactType).length > 1;

export const buildAvailableFields = ({
  layout,
  matterFields,
  draft,
  contactType,
}: {
  layout: GqlMatterLayout;
  matterFields: MatterField[];
  draft: NewFieldOptions[];
  contactType: ContactType;
}): AvailableField[] => {
  const draftNames = new Set(
    draft.map((field) => field.field?.name).filter(isNotNullOrUndefined),
  );
  const mapField = mapMatterField(layout);

  const candidates = new Map<string, MatterField>();
  matterFields
    .filter(
      (matterField) =>
        matterField.name.startsWith(`${contactType}/`) &&
        !draftNames.has(matterField.name) &&
        !candidates.has(matterField.name),
    )
    .forEach((matterField) => candidates.set(matterField.name, matterField));

  const rows = [...candidates.values()]
    .map((matterField) => {
      const mapped = mapField(matterField);
      if (!mapped) return undefined;

      return {
        name: matterField.name,
        label: buildFieldPathLabel(matterField.name, contactType),
        type: mapped.type,
        matterField,
      };
    })
    .filter(isNotNullOrUndefined);

  const [nested, flat] = partition(rows, (row) =>
    isNestedFieldName(row.name, contactType),
  );
  const byLabel = (a: AvailableField, b: AvailableField) =>
    a.label.localeCompare(b.label);

  return [...flat.sort(byLabel), ...nested.sort(byLabel)];
};

export const buildAddedField = ({
  layout,
  matterField,
  contactType,
}: {
  layout: GqlMatterLayout;
  matterField: MatterField;
  contactType: ContactType;
}): NewFieldOptions | undefined => {
  const mapped = mapMatterField(layout)(matterField);
  if (!mapped) return undefined;

  return {
    ...mapped,
    label: buildFieldPathLabel(matterField.name, contactType),
  };
};
